import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { withStyles } from '@material-ui/core';
import { FilledButton, Alert, Input, Textarea } from '@ds/react-components';
import { addSsoGroup } from 'services/ssoGroups';
import PageWrapper from 'components/ui/PageWrapper';
import RoleList from '../Role';
import * as Styled from '../common/SsoGroupForm.styled';

const styles = (theme) => ({});

const SsoGroupAdd = (props) => {
  const { roles, getSsoRoles } = props;
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [permissionsCount, setPermissionsCount] = useState(0);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const history = useHistory();
  const {
    handleSubmit,
    register,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      groupExternalId: '',
      description: '',
      permissions: [],
    },
  });

  useEffect(() => {
    const getRoles = async () => {
      await getSsoRoles();
    };
    getRoles();
  }, [getSsoRoles]);

  useEffect(() => {
    setPermissionsCount(selectedRoles.length);
    setValue('permissionsCount', selectedRoles.length);
  }, [setValue, selectedRoles]);

  const onCancel = (e) => {
    e.preventDefault();
    history.push(`/SsoGroupManagement`);
  };

  const onSubmit = async (data) => {
    const { name, groupExternalId, description } = data;
    const payload = {
      name,
      groupExternalId,
      description,
      permissions: selectedRoles.map((id) => {
        return { roleId: id };
      }),
    };

    try {
      setIsSubmittingForm(true);
      await addSsoGroup(payload);
      history.push(`/SsoGroupManagement`);
    } catch (error) {
      setIsSubmittingForm(false);
      setSubmissionError(
        error.message || 'Something went wrong. Please try again.'
      );
      console.error(error);
    }
  };

  return (
    <PageWrapper>
      {!!submissionError && (
        <Styled.InfoAlert variant={'error'} role={'banner'} dismissible={false}>
          <Alert.AlertMessage data-testid={`submission-error`}>
            {submissionError}
          </Alert.AlertMessage>
        </Styled.InfoAlert>
      )}
      <Styled.PageTitle>{'Add Group'}</Styled.PageTitle>
      <Styled.FormContainer>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Styled.Paper>
            <Styled.PaperHeader>
              <Styled.PaperHeaderText>{'Group Details'}</Styled.PaperHeaderText>
            </Styled.PaperHeader>
            <Styled.PaperBodyOverload>
              <Styled.InputWrapper>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: 'Name is required.',
                    },
                    maxLength: {
                      value: 50,
                      message: 'Name must be no longer than 50 characters.',
                    },
                  }}
                  render={({ onChange, name, value }) => (
                    <>
                      <Input
                        name={name}
                        value={value}
                        label="Name (Max 50 chars)"
                        maxlength={50}
                        onChange={(event) => {
                          onChange(event.target.value);
                        }}
                        error={errors.name && errors.name.message}
                      />
                    </>
                  )}
                />
              </Styled.InputWrapper>
              <Styled.InputWrapper>
                <Controller
                  name="groupExternalId"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: 'External ID is required.',
                    },
                    maxLength: {
                      value: 50,
                      message:
                        'External ID must be no longer than 50 characters.',
                    },
                  }}
                  render={({ onChange, name, value }) => (
                    <>
                      <Input
                        name={name}
                        value={value}
                        label="External ID (Max 50 chars)"
                        helpText="Object identifier from the company's Active Directory."
                        maxlength={50}
                        onChange={(event) => {
                          onChange(event.target.value);
                        }}
                        error={
                          errors.groupExternalId &&
                          errors.groupExternalId.message
                        }
                      />
                    </>
                  )}
                />
              </Styled.InputWrapper>
              <Styled.InputWrapper>
                <Controller
                  name="description"
                  control={control}
                  rules={{
                    maxLength: {
                      value: 500,
                      message: 'Description must not be more than 500.',
                    },
                  }}
                  render={({ onChange, name, value }) => (
                    <>
                      <Textarea
                        name={name}
                        value={value}
                        label="Description (Optional) (Max 500 chars)"
                        maxlength={500}
                        resizeable={false}
                        rows={3}
                        onChange={(event) => {
                          onChange(event.target.value);
                        }}
                        error={errors.description && errors.description.message}
                      />
                    </>
                  )}
                />
              </Styled.InputWrapper>
            </Styled.PaperBodyOverload>
          </Styled.Paper>
          <Styled.Paper>
            <Styled.PaperHeader>
              <Styled.PaperHeaderText>
                {'Permissions (' + selectedRoles.length + ' selected)'}
              </Styled.PaperHeaderText>
            </Styled.PaperHeader>
            <Styled.HiddenInputWrapper>
              <Input
                name="permissionsCount"
                value={permissionsCount}
                inputRef={register('permissionsCount', {
                  min: { value: 1 },
                })}></Input>
            </Styled.HiddenInputWrapper>
            {errors.permissionsCount && errors.permissionsCount.type === 'min' && (
              <Styled.PermissionsErrorMessage>
                <label>{'Select at least one permission.'}</label>
              </Styled.PermissionsErrorMessage>
            )}
            <RoleList
              roles={roles}
              selectedRoles={selectedRoles}
              setSelectedRoles={setSelectedRoles}
            />
          </Styled.Paper>
          <Styled.ActionButtonContainer>
            <FilledButton
              variant={'primary'}
              type={'submit'}
              isDisabled={isSubmittingForm}>
              {'Save'}
            </FilledButton>
            <Styled.CancelButton
              variant={'slighter'}
              onClick={onCancel}
              isDisabled={isSubmittingForm}>
              {'Cancel'}
            </Styled.CancelButton>
          </Styled.ActionButtonContainer>
        </form>
      </Styled.FormContainer>
    </PageWrapper>
  );
};

SsoGroupAdd.propTypes = {
  appLoading: PropTypes.bool.isRequired,
  roles: PropTypes.arrayOf(PropTypes.object),
};

SsoGroupAdd.defaultValues = {
  roles: [],
};

export default withStyles(styles)(SsoGroupAdd);
